import { LocalizedTextIds } from '../config/localizedTextIds.js';
import {
	DynamicChoiceKey,
	EditItemType,
	UpdateConversion,
} from '../entity/company-update.interface.js';
import {
	getMetadataRaw,
	getUpdateableFieldsRaw,
	getUpdateOptionsRaw,
	Metadata,
	Updateable,
} from './decorators.js';
import { DeliverablePartner } from './deliverablePartner.js';

export type AssetKey = keyof Asset;

export class Asset {
	@Metadata({ header: LocalizedTextIds.CompanyName })
	public companyName: string;

	/** The ID of the company the asset is associated with */
	public modelId?: string;
	public assetId: string;

	@Metadata({ header: LocalizedTextIds.AssetName })
	@Updateable({
		updateConversion: UpdateConversion.CorrectedWhitespace,
		maxLength: 80,
		required: true,
	})
	public name: string;

	@Metadata({ header: LocalizedTextIds.DeliverableType })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'deliverableType',
	})
	public deliverableType?: string;

	@Metadata({ header: LocalizedTextIds.Sector })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		labelTextId: LocalizedTextIds.PrimarySectorQuestion,
		referenceDataKey: 'sectors',
		required: true,
	})
	public sector: string;

	@Metadata({ header: LocalizedTextIds.FocusArea })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		dynamicChoiceKey: DynamicChoiceKey.FocusArea,
		required: true,
		preconditionProp: 'sector',
	})
	public focusArea: string;

	@Metadata({ header: LocalizedTextIds.TherapeuticArea })
	@Updateable({
		editType: EditItemType.MultiModal,
		referenceDataKey: 'therapeuticAreas',
		updateConversion: UpdateConversion.DelimitedString,
	})
	// FUTURE - Do we want to add the ability to sort on ingest
	// so sorting by TA is more predicatble to the user?
	public therapeuticArea: string[];

	@Metadata({ header: LocalizedTextIds.ClinicalIndication })
	@Updateable({
		editType: EditItemType.Default,
		maxLength: 500,
	})
	public indication: string;

	@Metadata({ header: LocalizedTextIds.Target })
	@Updateable({})
	public target?: string;

	@Metadata({ header: LocalizedTextIds.RouteOfAdministration })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'routeOfAdministration',
	})
	public routeOfAdministration?: string;

	@Metadata({ header: LocalizedTextIds.Keywords })
	@Updateable({
		editType: EditItemType.MultiModal,
		updateConversion: UpdateConversion.DelimitedString,
		referenceDataKey: 'keywords',
	})
	public keywords?: string[];

	@Metadata({ header: LocalizedTextIds.DevelopmentStage })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		dynamicChoiceKey: DynamicChoiceKey.DevelopmentStage,
		required: true,
		preconditionProp: 'sector',
	})
	public developmentStage?: string;

	@Metadata({ header: LocalizedTextIds.OperatingCountry })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'clinicalTrialCountries',
	})
	public clinicalOperatingCountry?: string;

	@Metadata({ header: LocalizedTextIds.ClinicalTrialNCTNumber })
	@Updateable({ maxLength: 50 })
	public clinicalTrialNctNumber?: string;

	@Metadata({ header: LocalizedTextIds.RegulatoryApprovalStatus })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'regulatoryApprovalStatus',
	})
	public regulatoryApprovalStatus?: string;

	@Metadata({ header: LocalizedTextIds.DeliverablePartner })
	public deliverablePartners?: DeliverablePartner[];

	@Metadata({ header: LocalizedTextIds.MedicalDeviceCategorization })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'medicalDeviceCategorization',
	})
	public medicalDeviceCategory?: string;

	@Metadata({ header: LocalizedTextIds.MedicalDeviceFDACategory })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'medicalDeviceFdaCategory',
	})
	public medicalDeviceFdaCategory?: string;

	@Metadata({ header: LocalizedTextIds.CurrentParteringStatus })
	@Updateable({
		editType: EditItemType.ConstrainedValue,
		referenceDataKey: 'currentPartneringStatus',
	})
	public currentPartneringStatus?: string;

	public isDeleted: boolean = false;

	public static CompareByPropery(
		a: Asset,
		b: Asset,
		sortKey: AssetKey
	): -1 | 1 | 0 {
		if (a[sortKey] < b[sortKey]) {
			return -1;
		}
		if (a[sortKey] > b[sortKey]) {
			return 1;
		}
		return 0;
	}

	public static SortByProperty(
		assets: Asset[],
		sortKey: AssetKey,
		direction: 'asc' | 'desc' = 'asc'
	) {
		assets.sort((a, b) => {
			return direction === 'asc'
				? Asset.CompareByPropery(a, b, sortKey)
				: Asset.CompareByPropery(b, a, sortKey);
		});
	}
}

// FUTURE - It might be nice to take a second pass at the metadata code and make it more generic tro avoid this

export function getAssetMetadata(propertyKey: AssetKey): Metadata {
	return getMetadataRaw(propertyKey, Asset.prototype) ?? {};
}

export function getAssetUpdateOptions(propertyKey: AssetKey) {
	return getUpdateOptionsRaw(propertyKey, Asset.prototype) ?? {};
}

export function getUpdateablAssetFields(): AssetKey[] {
	return getUpdateableFieldsRaw(Asset.prototype);
}

export function getAssetFieldsWithConversions(): AssetKey[] {
	return getUpdateablAssetFields().filter(
		(field) => !!getAssetUpdateOptions(field)?.updateConversion
	);
}

export function getDependentAssetFields(key: AssetKey): AssetKey[] {
	return (
		getUpdateablAssetFields().filter(
			(field) => getAssetUpdateOptions(field)?.preconditionProp === key
		) ?? []
	);
}

export function hasRequiredChanges(asset: Asset): boolean {
	for (const updatableField of getUpdateablAssetFields()) {
		if (asset.deliverablePartners.some((p) => !p.partnerName || !p.role)) {
			return true;
		}

		const { required } = getAssetUpdateOptions(updatableField);
		if (
			required &&
			(asset[updatableField] === null ||
				(asset[updatableField] as string).length === 0)
		) {
			return true;
		}
	}

	return false;
}
